/**
 * How much SOL a wallet has locked as rent in its token accounts, read with
 * two plain JSON-RPC calls through Solstack's RPC relay (worker/rpcProxy.js)
 * — no web3.js, so the marketing pages that show it stay light. Counts the
 * way Rent Reclaim does (findEmptyAccounts in solstack/lib/accounts.js): an
 * account holding 0 that isn't frozen closes as it is; one still holding
 * tokens closes only once they're burned or sold. On mainnet the tokens
 * still held are priced too, so dust (burned for its rent) is told apart
 * from tokens worth selling (summarizeWithDust).
 */
const TOKEN_PROGRAMS = ["TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA", "TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb"];
const ADDRESS_RE = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
const NOT_AN_ADDRESS = "That doesn't look like a Solana wallet address.";

export const looksLikeAddress = (s) => ADDRESS_RE.test(String(s || "").trim());

/** Rent SOL, 4 decimals, from lamports. */
export const rentSol = (lamports) => (lamports / 1e9).toFixed(4);

/** Counts and rent of `accounts` ({ amountRaw, frozen, lamports }), split into empty and still-holding. */
export function summarizeRent(accounts) {
  const out = { empty: 0, emptyLamports: 0, held: 0, heldLamports: 0, frozen: 0 };
  for (const a of accounts) {
    if (a.amountRaw !== "0") {
      out.held += 1;
      out.heldLamports += a.lamports;
    } else if (a.frozen) {
      out.frozen += 1;
    } else {
      out.empty += 1;
      out.emptyLamports += a.lamports;
    }
  }
  return out;
}

// Under this many dollars a holding is dust: what Rent Reclaim's "Select
// under $0.50" burns for its rent (solstack/lib/holdings.js DUST_USD).
export const DUST_USD = 0.5;
const JUP_PRICE = "https://lite-api.jup.ag/price/v3";
const PRICE_TIMEOUT_MS = 8000;
const PRICE_BATCH = 50; // mints per call, both sources

const timedFetch = async (url) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PRICE_TIMEOUT_MS);
  try {
    return await fetch(url, { signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
};

const batches = (list) => Array.from({ length: Math.ceil(list.length / PRICE_BATCH) }, (_, i) => list.slice(i * PRICE_BATCH, (i + 1) * PRICE_BATCH));

// USD price per mint: Birdeye through /api/prices (worker/prices.js), then
// Jupiter for the mints it has none for, as the app's holdings list does.
// null when the first lookup fails, since every token would then look like dust.
async function usdPrices(mints) {
  const out = {};
  try {
    const bodies = await Promise.all(
      batches(mints).map(async (ids) => {
        const res = await timedFetch(`/api/prices?mints=${ids.join(",")}`);
        if (!res.ok) throw new Error("prices");
        return res.json();
      })
    );
    bodies.forEach((body) => {
      if (!body?.prices) throw new Error("prices");
      Object.entries(body.prices).forEach(([mint, p]) => Number.isFinite(p?.price) && (out[mint] = p.price));
    });
  } catch {
    return null;
  }
  await Promise.all(
    batches(mints.filter((m) => !(m in out))).map(async (ids) => {
      try {
        const res = await timedFetch(`${JUP_PRICE}?ids=${ids.join(",")}`);
        if (!res.ok) return;
        Object.entries((await res.json()) || {}).forEach(([mint, row]) => Number.isFinite(row?.usdPrice) && (out[mint] = row.usdPrice));
      } catch {
        /* those mints stay unpriced */
      }
    })
  );
  return out;
}

/**
 * summarizeRent, with the accounts still holding tokens split the way Rent
 * Reclaim's "Select under $0.50" picks them: `dust` (a token worth under
 * DUST_USD in that account, or with no price: burned for its rent), `valued`
 * (worth more: sold for SOL or kept; `valuedUsd` in all) and `other` (what
 * it never picks: a likely NFT, wrapped SOL). A frozen account can't close,
 * so it's in none of them. `priced` is false when the price lookup failed.
 * `accounts` also need { mint, amount, decimals, isNative }.
 */
export async function summarizeWithDust(accounts) {
  const out = { ...summarizeRent(accounts), priced: false, dust: 0, dustLamports: 0, valued: 0, valuedLamports: 0, valuedUsd: 0, other: 0, otherLamports: 0 };
  const held = accounts.filter((a) => a.amountRaw !== "0" && !a.frozen);
  const nftLike = (a) => a.decimals === 0 && a.amountRaw === "1";
  const tokens = held.filter((a) => !a.isNative && !nftLike(a));
  const prices = tokens.length ? await usdPrices([...new Set(tokens.map((a) => a.mint))]) : {};
  if (!prices) return out;
  out.priced = true;
  held.forEach((a) => {
    if (a.isNative || nftLike(a)) {
      out.other += 1;
      out.otherLamports += a.lamports;
      return;
    }
    const usd = Number.isFinite(prices[a.mint]) ? prices[a.mint] * a.amount : 0;
    if (usd < DUST_USD) {
      out.dust += 1;
      out.dustLamports += a.lamports;
    } else {
      out.valued += 1;
      out.valuedLamports += a.lamports;
      out.valuedUsd += usd;
    }
  });
  return out;
}

const plural = (n, word) => `${n.toLocaleString("en-US")} ${word}${n === 1 ? "" : "s"}`;

/**
 * A check result in a line, the same on the home pages and in Rent Reclaim:
 * `lamports`, the headline (empty accounts, plus dust when it's priced), and
 * `text`, where it comes from. Tokens worth keeping or selling aren't
 * mentioned: the tool cleans out dust, not the whole wallet. The address
 * isn't repeated: it's in the box above.
 */
export function describeCheck(r) {
  if (!r.priced) {
    return {
      lamports: r.emptyLamports,
      text: `${r.empty ? `From ${plural(r.empty, "empty account")}.` : "No empty accounts."}${
        r.held ? ` ${plural(r.held, "more account")} still ${r.held === 1 ? "holds" : "hold"} tokens.` : ""
      }`,
    };
  }
  const parts = [r.empty && plural(r.empty, "empty account"), r.dust && `${plural(r.dust, "dust token")} under $${DUST_USD.toFixed(2)}`].filter(Boolean);
  return {
    lamports: r.emptyLamports + r.dustLamports,
    text: parts.length ? `From ${parts.join(" and ")}.` : "No empty accounts or dust.",
  };
}

/** summarizeWithDust (summarizeRent off mainnet) for `address` on `network`, straight from the chain. Throws a readable Error. */
export async function checkRent(address, network = "mainnet") {
  const owner = String(address || "").trim();
  if (!looksLikeAddress(owner)) throw new Error(NOT_AN_ADDRESS);
  const lists = await Promise.all(
    TOKEN_PROGRAMS.map(async (programId) => {
      let res;
      try {
        res = await fetch(`/api/rpc/${network}`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            jsonrpc: "2.0",
            id: 1,
            method: "getTokenAccountsByOwner",
            params: [owner, { programId }, { encoding: "jsonParsed", commitment: "confirmed" }],
          }),
        });
      } catch {
        throw new Error("Couldn't reach Solana just now. Try again in a moment.");
      }
      if (res.status === 429) throw new Error("Too many checks from this network. Wait a minute and try again.");
      const body = await res.json().catch(() => null);
      if (body?.error && /invalid param/i.test(body.error.message || "")) throw new Error(NOT_AN_ADDRESS);
      if (!res.ok || !body?.result) throw new Error("Couldn't reach Solana just now. Try again in a moment.");
      return body.result.value.map(({ account }) => {
        const info = account.data?.parsed?.info;
        return {
          mint: info?.mint,
          amount: Number(info?.tokenAmount?.uiAmount || 0),
          amountRaw: info?.tokenAmount?.amount || "0",
          decimals: info?.tokenAmount?.decimals ?? 0,
          isNative: !!info?.isNative,
          frozen: info?.state === "frozen",
          lamports: account.lamports,
        };
      });
    })
  );
  return network === "mainnet" ? summarizeWithDust(lists.flat()) : { ...summarizeRent(lists.flat()), priced: false };
}
